import { useCallback } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

import { languageConfig, Languages, LanguageConfig, defaultLocaleCodes } from './config'

export const findLanguageConfig = (localeCode: Languages): LanguageConfig | null => {
  return languageConfig.find(language => language.localeCode === localeCode) || null
}

const normaliseLocaleCode = (localeCode: string): string => {
  const [language, country] = localeCode.split('-')
  return country ? [language, country.toUpperCase()].join('-') : language
}

const getBrowserLanguage = (): string => {
  return normaliseLocaleCode(window.navigator.language)
}

export const getBaseLanguageFromLocaleCode = (localeCode: string): string => {
  return normaliseLocaleCode(localeCode).split('-')[0]
}

export const getPreselectedLanguage = (): Languages => {
  const browserLanguage = getBrowserLanguage()
  const language = languageConfig.find(language => language.localeCode === browserLanguage)
  const defaultLocaleCode = defaultLocaleCodes[browserLanguage.split('-')[0]] || defaultLocaleCodes.others
  return language ? language.localeCode : defaultLocaleCode
}

export function useSelectedLanguage() {
  const location = useLocation()
  return location.pathname.split('/')[1] as Languages
}

export function useSwitchLanguage() {
  const history = useHistory()
  const location = useLocation()
  const selectedLanguage = location.pathname.split('/')[1] as Languages

  const switchLanguage = useCallback((language: Languages) => {
    const languageConfig = findLanguageConfig(language)

    if (!languageConfig) {
      throw new Error(`Language ${language} not found`)
    }

    const newLocation = location.pathname.replace(`/${selectedLanguage}`, `/${language}`)
    history.push(`${newLocation}${location.search}${location.hash}`)
  }, [history, location, selectedLanguage])

  return switchLanguage
}
